import { html, LitElement, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_TOAST_WRAPPER } from '../../../webcomponents.constants';
import { GzToast, ToastActionButtonMode, ToastEvents, ToastPositionX, ToastPositionY } from '../type';
import { GzToastElement } from './GzToastElement';

export class GzToastNotificationWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  x = ToastPositionX.RIGHT;

  @property({ type: String, attribute: true, reflect: true })
  y = ToastPositionY.BOTTOM;

  @query('#toastNotification')
  private readonly notification!: HTMLElement;

  @query('.toast-wrapper')
  wrapper!: HTMLElement;

  constructor() {
    super();
    this.addEventListener(ToastEvents.CLOSED, this.handleDeletedToast);
  }

  static get styles() {
    return styles;
  }

  static buildToastWrapperElement(config: GzToast, parentElement?: HTMLElement, stacked = true) {
    // The toastNotification will be inserted directly into the document body, unless a parent is specified
    const parent = parentElement ? parentElement : document.body;
    const previousToastWrapperInstance = parent.querySelector(
      WEB_COMPONENT_GZ_TOAST_WRAPPER
    ) as GzToastNotificationWebComponent;
    let toastNotificationWrapper: GzToastNotificationWebComponent;

    if (!previousToastWrapperInstance) {
      toastNotificationWrapper = new GzToastNotificationWebComponent();

      for (const prop in config) {
        if (config.hasOwnProperty(prop)) {
          toastNotificationWrapper[prop] = config[prop];
        }
      }

      parent.appendChild(toastNotificationWrapper);
    }

    toastNotificationWrapper = parent.querySelector(WEB_COMPONENT_GZ_TOAST_WRAPPER) as GzToastNotificationWebComponent;
    toastNotificationWrapper.addNotificationToWrapper(config, stacked);
  }

  static closeAllToasts(): void {
    const toastNotificationWrapper = document.body.querySelector(
      WEB_COMPONENT_GZ_TOAST_WRAPPER
    ) as GzToastNotificationWebComponent;
    toastNotificationWrapper?.remove();
  }

  render(): TemplateResult {
    return html` <div class="toast-wrapper"></div> `;
  }

  private handleDeletedToast(): void {
    if (this.shadowRoot) {
      const childrenNumber = this.shadowRoot.querySelector('.toast-wrapper')?.childElementCount;
      if (childrenNumber === 1) {
        const toastNotificationWrapper = document.body.querySelector(
          WEB_COMPONENT_GZ_TOAST_WRAPPER
        ) as GzToastNotificationWebComponent;
        toastNotificationWrapper.remove();
      }
    }
  }

  private addNotificationToWrapper(toastConfig: GzToast, stacked) {
    const {
      message,
      severity,
      hasAction,
      actionCallback,
      keepOpen,
      canClose,
      autoCloseDelayMs,
      actionButtonText,
      actionButtonMode,
      showInlineButton,
    } = toastConfig;
    const toast = new GzToastElement();

    if (!stacked && this.wrapper) {
      if (this.wrapper.childElementCount > 0) {
        const toastChildren = Array.from(this.wrapper.children);
        toastChildren.forEach(el => el.remove());
      }
    }
    toast.message = message;
    toast.severity = severity;
    toast.hasAction = hasAction ?? false;
    toast.showInlineButton = showInlineButton ?? true;
    toast.actionButtonMode = actionButtonMode ?? ToastActionButtonMode.BUTTON;
    toast.actionButtonCallback = actionCallback;
    toast.keepOpen = keepOpen ? keepOpen : false;
    toast.canClose = canClose ?? true;

    if (autoCloseDelayMs) {
      toast.autoCloseDelayMs = autoCloseDelayMs;
    }
    if (actionButtonText) {
      toast.actionButtonText = actionButtonText;
    }
    this.updateComplete.then(() => this.wrapper.appendChild(toast));
  }
}
